<script setup lang="ts">
import { URL_IMAGE } from '@/config/constants'
import { categoryIdBus } from '@/event/category/categoryIdBus'
import { languageIdBus } from '@/event/languageIdBus'
import { productAssociated } from '@/event/product/productAssociated'
import { productBus } from '@/event/product/productBus'
import { productIdBus } from '@/event/product/productIdBus'
import { cardTheme } from '@/event/theme/card'
import {
    isOtherThemeHappy,
    isThemeDefault,
    isThemeHappy,
    theme
} from '@/event/theme/theme'
import CardEmptyProductHappy from '@/happyTheme/components/catalogCategory/cards/CardEmptyProductHappy.vue'
import CardFullProductHappy from '@/happyTheme/components/catalogCategory/cards/CardFullProductHappy.vue'
import { showAlertValidateProductHappy } from '@/happyTheme/event/product/showAlertValidateProductHappy'
import { showProductAssociated } from '@/happyTheme/event/product/showProductAssociated'
import { truncateText } from '@/happyTheme/utils/product/truncateText'
import type { Product } from '@/storage/products'
import { convertPriceToEuro } from '@/utils/price/convertPrice'
import { extractRelatedOptions } from '@/utils/product/extractRelatedOptions'
import { getProductDescription } from '@/utils/product/getProductDescription'
import { getProductDescriptionAssociated } from '@/utils/product/getProductDescriptionProductAssociated'
import { getProductName } from '@/utils/product/getProductName'
import { hasDescriptionForLanguage } from '@/utils/product/hasDescriptionForLanguage'
import { updateProduct } from '@/utils/product/updateProduct'
import { computed, nextTick, ref } from 'vue'

const expandedDescriptions = ref<boolean[]>([])
const descriptionWidths = ref<Record<number, number>>({})
const descriptionSpaces = ref<Record<number, number>>({})
const descriptionRefs = ref<HTMLDivElement[]>([])
const resizeObservers = ref<Record<number, ResizeObserver>>({})

function updateDescriptionWidth(index: number) {
    nextTick(() => {
        const container = descriptionRefs.value[index]
        if (!container) return

        const svg = container
            .closest('.div-product-associated-happy')
            ?.querySelector('.svg-card-product-associated-happy') as SVGElement

        if (!svg) return

        const svgRect = svg.getBoundingClientRect()
        const paddingLeft = window.getComputedStyle(container).paddingLeft
        const paddingRight = window.getComputedStyle(container).paddingRight
        const availableWidth =
            svgRect.width - parseFloat(paddingLeft) - parseFloat(paddingRight) - 20

        descriptionWidths.value[index] = availableWidth

        const h2Element = container.querySelector(
            '.h2-card-name-associated-happy'
        ) as HTMLElement
        if (h2Element) {
            const h2Height = h2Element.getBoundingClientRect().height
            const containerHeight = container.getBoundingClientRect().height
            const availableSpace = containerHeight - h2Height - 10
            descriptionSpaces.value[index] = availableSpace
        }
    })
}

function selectProductAssociated(product: Product) {
    // showSelectedModalProductHappy.value = false
    try {
        productIdBus.value = product.product_id
        categoryIdBus.value = product.category_id

        nextTick(() => {
            updateProduct()

            const productFound = productBus.value
            if (!productFound || !productFound.options) return
            extractRelatedOptions(productFound.options)

            expandedDescriptions.value = expandedDescriptions.value.map(() => false)
            showProductAssociated.value = false
            showAlertValidateProductHappy.value = false
        })
    } catch (error) {
        console.error('Error selecting product associated:', error)
    }
}

const setDescriptionRef = (el: HTMLDivElement | null) => {
    if (el) {
        if (!descriptionRefs.value.includes(el)) {
            descriptionRefs.value.push(el)
            updateDescriptionWidth(descriptionRefs.value.length - 1)

            if (!resizeObservers.value[descriptionRefs.value.length - 1]) {
                resizeObservers.value[descriptionRefs.value.length - 1] =
                    new ResizeObserver(() => {
                        updateDescriptionWidth(descriptionRefs.value.length - 1)
                    })
            }
            resizeObservers.value[descriptionRefs.value.length - 1].observe(el)
        }
    } else {
        const index = descriptionRefs.value.findIndex(
            (ref) => ref === null || !document.contains(ref)
        )
        if (index > -1) {
            descriptionRefs.value.splice(index, 1)
            if (resizeObservers.value[index]) {
                resizeObservers.value[index].disconnect()
                delete resizeObservers.value[index]
            }
        }
        // if (resizeObservers.value[descriptionRefs.value.length - 1]) {
        //     resizeObservers.value[descriptionRefs.value.length - 1].disconnect()
        //     delete resizeObservers.value[descriptionRefs.value.length - 1]
        // }
        // delete descriptionRefs.value[descriptionRefs.value.length - 1]
    }
}

const truncatedDescriptions = computed(() => {
    if (!productAssociated.value || productAssociated.value.length === 0) return []
    return productAssociated.value.map((pa, index) => {
        if (!pa) return ''
        const fullDesc = getProductDescription(pa)
        const maxWidth = descriptionWidths.value[index] || 0
        return truncateText(fullDesc, maxWidth)
    })
    // const fullDesc = getProductDescription(productAssociated.value[0])
    // const maxWidth = descriptionWidths.value[index] || 0
    // return truncateText(fullDesc, maxWidth)
})
</script>

<template>
    <section
        class="section-product-associated-main-content"
        v-if="productAssociated.length !== 0"
    >
        <!-- Theme Happy -->
        <section
            class="section-content-associated-happy"
            v-if="isThemeHappy || isOtherThemeHappy"
        >
            <h3 class="h3-content-associated-happy">Votre produit a bien été ajouté !</h3>

            <h3 class="h3-product-associated-happy">
                Les clients vous conseillent également ces produits pour accompagner votre
                sélection.
            </h3>

            <section
                class="section-product-associated-content-happy"
                :class="{
                    'gap-2': cardTheme !== 'rect',
                    'gap-y-4': cardTheme === 'rect'
                }"
            >
                <div
                    v-for="(pa, i) in productAssociated"
                    :key="i"
                    class="div-product-associated-happy"
                    @click.stop="selectProductAssociated(pa)"
                >
                    <CardEmptyProductHappy
                        v-if="cardTheme === 'empty'"
                        :product="pa"
                        :description="truncatedDescriptions[i]"
                    />
                    <CardFullProductHappy
                        v-else-if="cardTheme === 'full'"
                        :product="pa"
                        :description="truncatedDescriptions[i]"
                    />

                    <div
                        v-else-if="cardTheme === 'no-card'"
                        class="product-associated-happy-no-card"
                    >
                        <div class="w-full">
                            <img
                                loading="lazy"
                                :src="`${URL_IMAGE}${pa.image}`"
                                :alt="getProductName(pa)"
                                class="img-happy-no-card"
                            />

                            <div class="div-description-product-associated-no-card-happy">
                                <p class="text-product-associated-name-no-card-happy">
                                    {{ getProductName(pa) }}
                                </p>
                                <p class="text-price-product-associated-no-card-happy">
                                    {{ convertPriceToEuro(pa.price) + ' €' }}
                                </p>
                            </div>

                            <p
                                class="text-description-product-associated-no-card-happy"
                                v-if="getProductDescription(pa) !== 'Aucune description'"
                            >
                                {{ getProductDescription(pa) }}
                            </p>
                        </div>

                        <div class="px-2 w-full">
                            <div
                                class="div-button-select-product-associated-no-card-full-happy"
                            >
                                Sélectionner
                            </div>
                        </div>
                    </div>

                    <div
                        v-else-if="cardTheme !== 'empty' && cardTheme !== 'full'"
                        class="router-link-product-associated-happy"
                    >
                        <img
                            :src="`${URL_IMAGE}${pa.image}`"
                            class="img-for-product-associated-happy"
                            :alt="getProductDescription(pa)"
                            loading="lazy"
                        />

                        <svg
                            v-if="cardTheme === 'rect'"
                            class="happy-light:fill-bg-happy-light happy-dark:fill-text-basket-option-light-happy"
                            viewBox="0 0 281 215"
                            :class="{
                                'svg-card-product-rect-associated-happy':
                                    cardTheme === 'rect' || cardTheme !== 'rect'
                            }"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <g
                                :filter="
                                    'url(#' +
                                    (theme === 'happy-dark'
                                        ? `filter0_d_dark${pa.product_id}`
                                        : `filter0_d_light${pa.product_id}`) +
                                    ')'
                                "
                            >
                                <path
                                    d="M10 189.666V51.3125C10 44.4222 10 40.9771 11.9991 38.7095C12.2208 38.4581 12.4581 38.2208 12.7095 37.9991C14.9771 36 18.438 36 25.36 36H255.64C262.562 36 266.023 36 268.29 37.9991C268.542 38.2208 268.779 38.4581 269.001 38.7095C271 40.9771 271 44.438 271 51.36V189.64C271 196.562 271 200.023 269.001 202.29C268.779 202.542 268.542 202.779 268.29 203.001C266.023 205 262.562 205 255.64 205H25.36C18.438 205 14.9771 205 12.7095 203.001C12.4581 202.779 12.2208 202.542 11.9991 202.29C10 200.023 10 196.571 10 189.666Z"
                                />
                            </g>

                            <defs>
                                <filter
                                    :id="`filter0_d_light${pa.product_id}`"
                                    x="0"
                                    y="0.449219"
                                    filterUnits="userSpaceOnUse"
                                    color-interpolation-filters="sRGB"
                                >
                                    <feDropShadow
                                        dx="0"
                                        dy="1"
                                        stdDeviation="2"
                                        flood-color="currentColor"
                                        flood-opacity="0.7"
                                    />
                                </filter>

                                <filter
                                    :id="`filter0_d_dark${pa.product_id}`"
                                    x="0"
                                    y="0.449219"
                                    filterUnits="userSpaceOnUse"
                                    color-interpolation-filters="sRGB"
                                >
                                    <feDropShadow
                                        dx="0"
                                        dy="1"
                                        stdDeviation="2"
                                        flood-color="currentColor"
                                        flood-opacity="0.7"
                                    />
                                </filter>
                            </defs>
                        </svg>

                        <svg
                            v-else-if="cardTheme !== 'rect'"
                            class="svg-card-product-associated-happy"
                            viewBox="0 0 281 215"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <g
                                :filter="
                                    'url(#' +
                                    (theme === 'happy-dark'
                                        ? `filter0_d_dark${i}`
                                        : `filter0_d_light${i}`) +
                                    ')'
                                "
                            >
                                <path
                                    d="M10 164.61V52.2472C10 29.8176 10 18.6028 16.855 12.6248C17.6013 11.974 18.3949 11.3796 19.2292 10.8463C26.8931 5.94802 37.6474 9.098 59.1561 15.3979L243.415 69.3677C256.118 73.0886 262.47 74.949 266.369 79.6318C266.808 80.1589 267.22 80.7082 267.602 81.2776C271 86.3361 271 92.9618 271 106.213V164.6C271 181.905 271 190.557 266.002 196.226C265.448 196.855 264.855 197.448 264.226 198.002C258.557 203 249.905 203 232.6 203H48.4C31.0951 203 22.4426 203 16.7738 198.002C16.1451 197.448 15.552 196.855 14.9978 196.226C10 190.557 10 181.908 10 164.61Z"
                                />
                            </g>

                            <defs>
                                <filter
                                    :id="`filter0_d_light${i}`"
                                    x="0"
                                    y="0.449219"
                                    filterUnits="userSpaceOnUse"
                                    color-interpolation-filters="sRGB"
                                >
                                    <feDropShadow
                                        dx="0"
                                        dy="1"
                                        stdDeviation="2"
                                        flood-color="currentColor"
                                        flood-opacity="0.7"
                                    />
                                </filter>

                                <filter
                                    :id="`filter0_d_dark${i}`"
                                    x="0"
                                    y="0.449219"
                                    filterUnits="userSpaceOnUse"
                                    color-interpolation-filters="sRGB"
                                >
                                    <feDropShadow
                                        dx="0"
                                        dy="1"
                                        stdDeviation="2"
                                        flood-color="currentColor"
                                        flood-opacity="0.7"
                                    />
                                </filter>
                            </defs>
                        </svg>

                        <div class="div-informations-product-associated-happy">
                            <div class="div-card-price-associated-happy" @click="">
                                <svg
                                    class="svg-card-price-associated-happy"
                                    :class="{}"
                                    viewBox="0 0 50 37"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g filter="url(#filter0_d_169_5884)">
                                        <path
                                            d="M35.3654 30.1875H17.0775C12.335 30.1875 9.96372 30.1875 8.17523 29.214C6.82195 28.4773 5.71018 27.3655 4.97354 26.0123C4 24.2238 4 20.694 4 13.6343C4 9.92305 4 8.06742 5.32046 6.06409C6.31431 4.55628 7.78908 3.42908 9.50488 2.86584C11.7846 2.1175 14.6929 2.9086 20.5095 4.49079L38.1588 9.29165C40.008 9.79465 40.9326 10.0461 41.6979 10.4451C43.8015 11.5416 45.31 13.5152 45.8159 15.833C46 16.6761 46 17.635 46 19.5529C46 22.0029 46 23.2279 45.7339 24.2337C44.9982 27.0142 42.8267 29.1857 40.0462 29.9214C39.0404 30.1875 37.8154 30.1875 35.3654 30.1875Z"
                                        />
                                    </g>
                                    <defs>
                                        <filter
                                            id="filter0_d_169_5884"
                                            x="0"
                                            y="0.581055"
                                            filterUnits="userSpaceOnUse"
                                            color-interpolation-filters="sRGB"
                                        >
                                            <feFlood
                                                flood-opacity="0"
                                                result="BackgroundImageFix"
                                            />

                                            <feOffset dy="2" />
                                            <feGaussianBlur stdDeviation="2" />
                                            <feComposite in2="hardAlpha" operator="out" />
                                            <feColorMatrix
                                                type="matrix"
                                                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                                            />
                                            <feBlend
                                                mode="normal"
                                                in2="BackgroundImageFix"
                                                result="effect1_dropShadow_169_5884"
                                            />
                                            <feBlend
                                                mode="normal"
                                                in="SourceGraphic"
                                                in2="effect1_dropShadow_169_5884"
                                                result="shape"
                                            />
                                        </filter>
                                    </defs>
                                </svg>
                                <p class="p-card-price-associated-happy">
                                    {{ convertPriceToEuro(pa.price) }} €
                                </p>
                            </div>
                        </div>

                        <div
                            class="div-card-description-associated-happy"
                            :ref="(el) => setDescriptionRef(el as HTMLDivElement)"
                        >
                            <div class="div-card-name-associated-happy">
                                <h3 class="h2-card-name-associated-happy">
                                    {{ getProductName(pa) }}
                                </h3>
                                <p
                                    class="p-card-name-associated-happy"
                                    v-if="
                                        hasDescriptionForLanguage(
                                            pa.description,
                                            languageIdBus
                                        )
                                    "
                                    :class="{ 'show-full': expandedDescriptions }"
                                >
                                    {{ truncatedDescriptions[i] }}
                                </p>
                            </div>

                            <button class="div-select-product-associated-happy">
                                <svg
                                    class="svg-size-select-product-associated-happy"
                                    viewBox="0 0 18 14"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M8.99993 6.99976C9.17351 6.99976 9.31439 6.85718 9.31439 6.68157C9.31439 6.50593 9.17351 6.36338 8.99993 6.36338C8.82634 6.36338 8.68546 6.50593 8.68546 6.68157C8.68546 6.85718 8.82634 6.99976 8.99993 6.99976Z"
                                    />
                                    <path
                                        d="M15.7923 4.45429H13.9327L11.0721 0.472049C10.8151 0.0264576 10.2491 -0.13214 9.79948 0.119802C9.34376 0.375971 9.17989 0.956793 9.43316 1.41805C9.44042 1.43109 9.44841 1.44365 9.45737 1.45558L11.7011 4.45429H6.29876L8.54248 1.45558C8.55131 1.44365 8.55943 1.43109 8.56669 1.41805C8.81936 0.95791 8.65732 0.376592 8.2 0.119553C7.75142 -0.131892 7.18539 0.0265819 6.92791 0.471801L4.06718 4.45429H2.20755C1.51402 4.45429 0.949707 5.02528 0.949707 5.72705C0.949707 6.28051 1.30471 6.76241 1.80477 6.93292C1.85808 6.9747 1.92466 6.99981 1.99713 6.99981C2.01163 6.99981 2.02526 7.00067 2.03951 7.00267C2.17488 7.02106 2.28212 7.12495 2.30668 7.26095L3.37647 13.2163C3.45815 13.6704 3.84853 14 4.30473 14H13.6951C14.1513 14 14.5417 13.6704 14.6234 13.2162L15.6932 7.26108C15.7177 7.12495 15.8251 7.02106 15.9609 7.00267C15.9747 7.00067 15.9883 6.99981 16.0027 6.99981C16.0757 6.99981 16.1428 6.97445 16.1961 6.93219C16.6965 6.76053 17.0501 6.27952 17.0501 5.72705C17.0501 5.02528 16.4858 4.45429 15.7923 4.45429ZM9.97438 1.09227C9.90253 0.941752 9.95831 0.75842 10.1045 0.676388C10.2567 0.590998 10.4485 0.646058 10.5324 0.799063C10.5386 0.810499 10.5456 0.821438 10.5531 0.831878L13.1551 4.45429H12.4898L9.97438 1.09227ZM7.44677 0.832002C7.45428 0.821562 7.46101 0.810747 7.4673 0.799562C7.55142 0.646433 7.74343 0.591246 7.895 0.676139C8.04145 0.75842 8.09745 0.941504 8.02547 1.09227L5.51003 4.45429H4.84472L7.44677 0.832002ZM15.7923 6.36343H10.2578C10.0841 6.36343 9.94331 6.50585 9.94331 6.68162C9.94331 6.85736 10.0841 6.99656 10.2578 6.99656H15.113C15.0966 7.04404 15.0835 7.09326 15.0744 7.14398L14.0045 13.1024C13.9774 13.2537 13.8472 13.3636 13.6951 13.3636H4.30473C4.15256 13.3636 4.02246 13.2537 3.9952 13.1025L2.92541 7.1471C2.91632 7.09638 2.9033 7.04716 2.88684 6.99981H7.74208C7.91576 6.99981 8.05654 6.85736 8.05654 6.68162C8.05654 6.50585 7.91576 6.36343 7.74208 6.36343H2.20755C1.85784 6.36343 1.57863 6.07505 1.57863 5.72705C1.57863 5.37615 1.86079 5.09067 2.20755 5.09067H15.7923C16.1391 5.09067 16.4212 5.37615 16.4212 5.72705C16.4212 6.0804 16.1355 6.36343 15.7923 6.36343Z"
                                    />
                                    <path
                                        d="M8.99993 7.95433C8.47971 7.95433 8.05654 8.38252 8.05654 8.90891V11.4544C8.05654 11.9808 8.47971 12.409 8.99993 12.409C9.52014 12.409 9.94331 11.9808 9.94331 11.4544V8.90891C9.94331 8.38154 9.52112 7.95433 8.99993 7.95433ZM9.31439 11.4544C9.31439 11.6299 9.17338 11.7726 8.99993 11.7726C8.82647 11.7726 8.68546 11.6299 8.68546 11.4544V8.90891C8.68546 8.73339 8.82647 8.59071 8.99993 8.59071C9.17351 8.59071 9.31439 8.73326 9.31439 8.90891V11.4544Z"
                                    />
                                    <path
                                        d="M12.1445 12.409C12.6648 12.409 13.0879 11.9808 13.0879 11.4544V8.90891C13.0879 8.38154 12.6657 7.95433 12.1445 7.95433C11.6243 7.95433 11.2012 8.38252 11.2012 8.90891V11.4544C11.2012 11.9808 11.6243 12.409 12.1445 12.409ZM11.8301 8.90891C11.8301 8.73339 11.9711 8.59071 12.1445 8.59071C12.3181 8.59071 12.459 8.73326 12.459 8.90891V11.4544C12.459 11.6299 12.318 11.7726 12.1445 11.7726C11.9711 11.7726 11.8301 11.6299 11.8301 11.4544V8.90891Z"
                                    />
                                    <path
                                        d="M5.85531 7.95433C5.3351 7.95433 4.91192 8.38252 4.91192 8.90891V11.4544C4.91192 11.9808 5.3351 12.409 5.85531 12.409C6.37552 12.409 6.79869 11.9808 6.79869 11.4544V8.90891C6.79869 8.38154 6.3765 7.95433 5.85531 7.95433ZM6.16977 11.4544C6.16977 11.6299 6.02877 11.7726 5.85531 11.7726C5.68185 11.7726 5.54085 11.6299 5.54085 11.4544V8.90891C5.54085 8.73339 5.68185 8.59071 5.85531 8.59071C6.02889 8.59071 6.16977 8.73314 6.16977 8.90891V11.4544Z"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </section>

        <!-- Theme Default -->

        <section class="w-full flex flex-col flex-wrap" v-if="isThemeDefault">
            <h3 class="h3-main-product-associated">Produit associé(s)</h3>
            <section class="section-product-associated-content gap-2" :class="{}">
                <div
                    v-for="(pa, i) in productAssociated"
                    :key="i"
                    class="div-for-product-associated"
                >
                    <router-link
                        :to="{
                            name: 'product',
                            params: {
                                category_id: pa.category_id,
                                product_id: pa.product_id
                            }
                        }"
                        class="router-link-product-associated"
                    >
                        <img
                            :src="`${URL_IMAGE}${pa.image}`"
                            class="img-for-product-associated"
                            :alt="getProductDescription(pa)"
                            loading="lazy"
                        />
                        <h3 class="h3-product-associated">{{ getProductName(pa) }}</h3>
                        <div class="div-informations-product-associated">
                            <p class="p-product-associated">
                                {{ getProductDescriptionAssociated(pa) }}
                            </p>
                            <p>{{ convertPriceToEuro(pa.price) }} €</p>
                        </div>
                    </router-link>
                </div>
            </section>
        </section>
    </section>
</template>

<style scoped>
/** Theme Happy */

/** no card */

.product-associated-happy-no-card {
    @apply w-full flex flex-col justify-between h-full gap-1 rounded-md py-2 px-1;
}
.img-happy-no-card {
    @apply w-full h-28 object-contain rounded-t-md;
}
.div-description-product-associated-no-card-happy {
    @apply flex text-text-dark-happy-light justify-between px-3 mt-2;
}
.text-product-associated-name-no-card-happy {
    @apply font-semibold text-wrap text-sm;
}
.text-description-product-associated-no-card-happy {
    @apply px-3 text-xs line-clamp-2 text-text-dark-happy-light text-ellipsis;
}
.text-price-product-associated-no-card-happy {
    @apply font-semibold text-button-yellow-happy-light text-nowrap text-sm px-2;
}
.div-button-select-product-associated-no-card-full-happy {
    @apply bg-button-yellow-happy-light rounded-md flex;
    @apply items-center justify-center text-text-dark-happy-light font-semibold;
}

@media (hover) {
    .product-associated-happy-no-card:hover {
        @apply bg-red-eronor;
    }
    .product-associated-happy-no-card:hover .text-product-associated-name-no-card-happy,
    .product-associated-happy-no-card:hover
        .text-description-product-associated-no-card-happy {
        @apply text-bg-happy-light;
    }
}

/** */
.section-product-associated-content-happy {
    @apply grid grid-cols-2 justify-items-center;
}

.section-content-associated-happy {
    @apply w-full flex flex-col flex-wrap;
}

.h3-content-associated-happy {
    @apply font-semibold text-text-dark-happy-light text-lg pb-2 px-1;
    @apply happy-light:text-text-dark-happy-light;
    @apply happy-dark:text-text-dark-happy-light;
    @apply happy-urban-dark-red:text-bg-happy-light;
}

.h3-product-associated-happy {
    @apply font-semibold text-text-dark-happy-light text-base pb-6 px-1;
    @apply happy-light:text-text-dark-happy-light;
    @apply happy-dark:text-text-dark-happy-light;
    @apply happy-urban-dark-red:text-bg-happy-light;
}

.div-product-associated-happy {
    @apply w-full flex flex-col items-center justify-between;
}

.router-link-product-associated-happy {
    @apply w-full flex flex-col items-center flex-grow relative;
    @apply happy-light:shadow-text-dark-happy-light;
    @apply happy-dark:shadow-bg-happy-light;
}

.svg-card-product-associated-happy {
    @apply w-full h-auto;
    @apply happy-light:shadow-text-dark-happy-light happy-light:fill-bg-happy-light;
    @apply happy-dark:shadow-bg-happy-light happy-dark:fill-text-basket-option-light-happy;
    @apply happy-urban-dark-red:shadow-bg-happy-light happy-urban-dark-red:fill-urban-red;
}

.div-product-associated-happy:active .svg-card-product-associated-happy,
.div-product-associated-happy:focus .svg-card-product-associated-happy {
    transform: scale(1.05);
    transition: all 0.3s ease;
    @apply happy-light:fill-border-red-happy-light;
    @apply happy-dark:fill-border-red-happy-light;
    @apply happy-urban-dark-red:fill-urban-red-dark;
}

.div-product-associated-happy:active .svg-card-price-associated-happy,
.div-product-associated-happy:focus .svg-card-price-associated-happy {
    @apply happy-light:fill-mouse-enter-red-happy-light;
    @apply happy-dark:fill-mouse-enter-red-happy-light;
    @apply happy-urban-dark-red:fill-urban-dark;
}

.img-for-product-associated-happy {
    @apply inset-0 -top-6 -left-2 w-5/6 h-3/5 object-contain absolute z-10;
}
.div-informations-product-associated-happy {
    @apply absolute z-10 top-[28%] right-[3%] flex items-center justify-center;
}

.p-product-associated-happy {
    @apply absolute inset-0 flex text-sm items-center text-center m-auto font-semibold;
    @apply happy-light:text-bg-happy-light;
    @apply happy-dark:text-bg-happy-light;
    @apply happy-urban-dark-red:text-bg-happy-light;
}

.div-card-price-associated-happy {
    @apply absolute z-20 top-[28%] right-[3%] flex flex-col items-center;
    @apply cursor-pointer;
}

.svg-card-product-rect-associated-happy {
    @apply w-full h-auto;
}

.svg-card-price-associated-happy {
    @apply w-16 fill-border-gray-happy-light;
}

.p-card-price-associated-happy {
    @apply absolute text-sm inset-0 flex items-center justify-center text-center m-auto font-semibold;
}

.div-card-description-associated-happy {
    @apply absolute top-[45%] bottom-3 transform text-center min-w-full text-sm font-semibold flex flex-col items-start pl-3;
}

.div-card-name-associated-happy {
    @apply overflow-hidden w-[97%];
}

.h2-card-name-associated-happy {
    @apply text-wrap text-start w-3/5 text-ellipsis;
}

.p-card-name-associated-happy {
    @apply text-start text-xs w-4/6 mt-1 overflow-hidden;
}

@media (hover: hover) {
    .div-product-associated-happy:hover .svg-card-product-associated-happy {
        transform: scale(1.03);
        transition: all 0.3s ease;
        @apply happy-light:fill-border-red-happy-light;
        @apply happy-dark:fill-border-red-happy-light;
        @apply happy-urban-dark-red:fill-urban-red-dark;
    }
    .div-product-associated-happy:hover .svg-card-price-associated-happy {
        @apply happy-light:fill-mouse-enter-red-happy-light;
        @apply happy-dark:fill-mouse-enter-red-happy-light;
        @apply happy-urban-dark-red:fill-urban-dark;
    }
}

/** Theme Default */

.section-product-associated-main-content {
    @apply mt-3 rounded-md flex flex-col gap-2;
}

.h3-main-product-associated {
    @apply font-semibold text-lg flex w-full items-center pb-2;
}

.section-product-associated-content {
    @apply grid grid-cols-2;
    grid-auto-rows: minmax(11rem, auto);
}

.div-for-product-associated {
    @apply flex flex-col items-center justify-between;
}

.router-link-product-associated {
    @apply w-full flex flex-col items-center p-3 flex-grow bg-slate-eronor rounded-md;
}

.img-for-product-associated {
    @apply w-5/6 h-3/5 object-contain absolute z-10;
}

.h3-product-associated {
    @apply w-full font-medium;
}

.div-informations-product-associated {
    @apply w-full font-normal;
}

.p-product-associated {
    @apply text-[10px];
}

@media screen and (min-width: 600px) {
    .section-product-associated-content {
        @apply grid-cols-3;
    }
}

@media screen and (min-width: 800px) {
    .section-product-associated-content {
        @apply grid-cols-4;
    }
}
</style>
